import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CacheExpiryConstants, CacheKeyConstants, CacheService, isNullOrUndefined } from '@uzurv/core';
// eslint-disable-next-line import/order
import { UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize } from 'cerialize';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { State } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class StateService extends UzurvRestService {
  readonly resourceUrl = 'states/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer,
    private cacheService: CacheService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Returns a list of states
   *
   * @param overrideCache
   */
  getStates(overrideCache = false): Observable<Array<State>> {
    const cachedData = this.cacheService.getCachedData(CacheKeyConstants.STATES);
    if (!overrideCache && !isNullOrUndefined(cachedData)) {
      return of(Deserialize(JSON.parse(cachedData), State));
    }

    return this.get<Array<State>>(this.resourceUrl, {
      pageNumber: 0,
      pageSize: 100
    }).pipe(
      map(response => {
        this.cacheService.cacheData(CacheKeyConstants.STATES, JSON.stringify(response), CacheExpiryConstants.DOMAIN_CACHE_EXPIRY);
        return Deserialize(response, State);
      })
    );
  }

  /**
   * Returns a state by ID
   *
   * @param id
   * @param overrideCache
   */
  findStateById(id: number, overrideCache = false): Observable<State> {
    return this.getStates(overrideCache).pipe(map(response => response.find(state => state.id === id)));
  }

  /**
   * Returns a state by code
   *
   * @param name
   * @param overrideCache
   */
  findStateByCode(code: string, overrideCache = false): Observable<State> {
    return this.getStates(overrideCache).pipe(map(response => response.find(state => state.code === code)));
  }
}
